import React from 'react';
import {
  LayoutDashboard,
  BarChart3,
  Bot,
  Users,
  CreditCard,
  FileText,
  Layers,
  Sparkles,
  RefreshCw,
  ShoppingCart,
  UserCheck,
  Code2,
  Settings as SettingsIcon,
  LogOut,
} from 'lucide-react';

interface SidebarProps {
  activeView: string;
  setActiveView: (view: string) => void;
  onLogout?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ activeView, setActiveView, onLogout }) => {
  const menuGroups = [
    {
      title: 'Overview',
      items: [
        { id: 'dashboard', label: 'Home', icon: LayoutDashboard },
        { id: 'reports', label: 'Analytics', icon: BarChart3 },
        { id: 'ai', label: 'Smart Advisor', icon: Bot },
      ],
    },
    {
      title: 'Billing Operations',
      items: [
        { id: 'customers', label: 'Customers', icon: Users },
        { id: 'subscriptions', label: 'Subscriptions', icon: CreditCard },
        { id: 'invoices', label: 'Invoices', icon: FileText },
        { id: 'plans', label: 'Plans & Pricing', icon: Layers },
        { id: 'customisation', label: 'Checkout Studio', icon: Sparkles },
      ],
    },
    {
      title: 'Recovery & Portal',
      items: [
        { id: 'dunning', label: 'Payment Recovery', icon: RefreshCw },
        { id: 'abandoned', label: 'Lost Carts', icon: ShoppingCart },
        { id: 'customer_portal', label: 'Customer Portal', icon: UserCheck },
      ],
    },
    {
      title: 'Settings',
      items: [
        { id: 'api', label: 'Developer API', icon: Code2 },
        { id: 'settings', label: 'Platform Settings', icon: SettingsIcon },
      ],
    },
  ];

  return (
    <aside className="w-56 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 h-screen sticky top-0 flex flex-col hidden md:flex shrink-0 transition-colors duration-200">
      {/* Brand Header */}
      <div className="p-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 bg-indigo-600 rounded-lg flex items-center justify-center text-white font-black text-sm shadow-sm">
            S
          </div>
          <div>
            <span className="text-sm font-black text-slate-900 dark:text-white tracking-tight">SubScale</span>
            <p className="text-[10px] text-slate-400 font-medium leading-none mt-0.5">Billing Engine</p>
          </div>
        </div>
      </div>

      {/* Clean Navigation Menu */}
      <div className="flex-1 overflow-y-auto px-2.5 py-3 space-y-4 no-scrollbar">
        {menuGroups.map((group, idx) => (
          <div key={idx}>
            <h3 className="px-2 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">
              {group.title}
            </h3>
            <div className="space-y-0.5">
              {group.items.map((item) => {
                const Icon = item.icon;
                const isActive = activeView === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveView(item.id)}
                    className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${
                      isActive
                        ? 'bg-indigo-600 text-white shadow-xs font-bold'
                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:text-slate-200'
                    }`}
                  >
                    <Icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-white' : 'text-slate-400'}`} />
                    <span className="truncate">{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* User / Workspace Footer */}
      <div className="p-2.5 border-t border-slate-100 dark:border-slate-800 shrink-0">
        <div className="bg-slate-50 dark:bg-slate-800 p-2 rounded-xl flex items-center justify-between">
          <div className="flex items-center gap-2 overflow-hidden">
            <img
              src="https://picsum.photos/40/40?u=admin"
              className="w-7 h-7 rounded-md border border-slate-200 dark:border-slate-700 shrink-0"
              alt="User"
            />
            <div className="truncate">
              <p className="text-xs font-bold text-slate-900 dark:text-white truncate">Alex Rivera</p>
              <p className="text-[10px] text-slate-400 truncate">Admin</p>
            </div>
          </div>
          <button
            onClick={onLogout}
            className="p-1.5 text-slate-400 hover:text-red-600 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-all"
            title="Log out"
          >
            <LogOut className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
